import { Children, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import NavGroupHeader from "./NavGroupHeader";

const finePointer = () => typeof window !== "undefined" && window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;

// One collapsible category (accordion, controlled by Sidebar): tap/click toggles.
// Desktop mouse/keyboard only (flyout=true): a COLLAPSED group previews its items in a flyout next to the sidebar.
export default function NavGroup({ group, open, active, onToggle, flyout = false, children }) {
  const [hover, setHover] = useState(false);
  const [peek, setPeek] = useState(false);
  const [pos, setPos] = useState(null);
  const wrap = useRef(null);
  const menu = useRef(null);
  const timers = useRef({});
  const rgb = group.glowRgb || "var(--glow-home)";
  const kids = Children.toArray(children);
  const slug = (group.slug || group.label).toLowerCase();
  const can = flyout && !open;

  const clear = () => { clearTimeout(timers.current.open); clearTimeout(timers.current.close); };
  const show = (delay) => { if (!can) return; clear(); timers.current.open = setTimeout(() => setPeek(true), delay); };
  const hide = (delay = 200) => { clear(); timers.current.close = setTimeout(() => setPeek(false), delay); };
  useEffect(() => () => clear(), []);
  useEffect(() => { if (open) { clear(); setPeek(false); } }, [open]);
  useEffect(() => {
    if (!peek) return undefined;
    const esc = (e) => { if (e.key === "Escape") { clear(); setPeek(false); } };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [peek]);

  useLayoutEffect(() => {
    if (!peek || !wrap.current) return;
    menu.current?.querySelectorAll("button, a").forEach((b) => b.setAttribute("role", "menuitem"));
    const r = wrap.current.getBoundingClientRect();
    const h = menu.current?.offsetHeight || 0;
    setPos({ left: r.right + 8, top: Math.max(8, Math.min(r.top - 6, window.innerHeight - h - 8)) });
  }, [peek, kids.length]);

  const focusItem = (dir) => {
    const items = [...(menu.current?.querySelectorAll("button, a") || [])];
    const i = items.indexOf(document.activeElement);
    items[(i + dir + items.length) % items.length]?.focus();
  };
  const onMenuKey = (e) => {
    if (e.key === "Escape" || e.key === "ArrowLeft") { e.preventDefault(); setPeek(false); wrap.current?.querySelector("button")?.focus(); }
    if (e.key === "ArrowDown") { e.preventDefault(); focusItem(1); }
    if (e.key === "ArrowUp") { e.preventDefault(); focusItem(-1); }
  };
  const onHeaderKey = (e) => {
    if (!can) return;
    if (e.key === "ArrowRight") { e.preventDefault(); setPeek(true); setTimeout(() => menu.current?.querySelector("button, a")?.focus(), 30); }
    if (e.key === "Escape") setPeek(false);
  };

  const portalTarget = typeof document !== "undefined" ? document.querySelector("[data-testid='app-shell']") || document.body : null;

  return (
    <div
      ref={wrap}
      className="mb-0.5"
      onMouseEnter={() => { if (finePointer()) { setHover(true); show(250); } }}
      onMouseLeave={() => { setHover(false); if (flyout) hide(); }}
      onFocus={(e) => { if (e.target === wrap.current?.querySelector("button") && e.target.matches(":focus-visible")) show(0); }}
      onBlur={(e) => { if (flyout && !menu.current?.contains(e.relatedTarget) && !wrap.current?.contains(e.relatedTarget)) hide(150); }}
      onKeyDown={onHeaderKey}
      data-open={open ? "1" : "0"}
      data-testid={`nav-groupwrap-${slug}`}
    >
      <NavGroupHeader group={group} open={open} hovered={hover || peek} active={active} onToggle={() => { clear(); setPeek(false); onToggle(); }} />
      <motion.div
        initial={false}
        animate={open ? { height: "auto", opacity: 1, visibility: "visible" } : { height: 0, opacity: 0, transitionEnd: { visibility: "hidden" } }}
        transition={{ height: { type: "spring", stiffness: 520, damping: 34, mass: 0.8 }, opacity: { duration: 0.16 } }}
        style={{ overflow: "hidden" }}
        aria-hidden={!open}
      >
        <div className="pb-1.5 pl-1.5 ml-3 space-y-0.5 border-l" style={{ borderColor: `rgba(${rgb},0.18)` }}>
          {kids.map((child, i) => (
            <motion.div key={child.key ?? i} initial={false} animate={{ x: open ? 0 : -6, opacity: open ? 1 : 0 }}
              transition={{ delay: open ? 0.03 + Math.min(i, 12) * 0.022 : 0, type: "spring", stiffness: 500, damping: 32 }}>
              {child}
            </motion.div>
          ))}
        </div>
      </motion.div>
      {peek && can && portalTarget && createPortal(
        <div
          ref={menu}
          role="menu"
          aria-label={group.label}
          className="q-chrome fixed z-[80] w-60 max-h-[calc(100vh-16px)] overflow-y-auto rounded-xl border border-q-border bg-q-surface p-1.5 shadow-2xl"
          style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999, boxShadow: `0 18px 40px -12px rgba(0,0,0,0.45), 0 0 0 1px rgba(${rgb},0.16)` }}
          onMouseEnter={clear}
          onMouseLeave={() => hide()}
          onKeyDown={onMenuKey}
          onClickCapture={() => setTimeout(() => setPeek(false), 0)}
          data-testid={`nav-flyout-${slug}`}
        >
          <p className="px-2 pb-1 pt-0.5 text-[10.5px] font-bold uppercase tracking-[0.14em]" style={{ color: `rgb(${rgb})` }}>{group.label}</p>
          {kids.map((child, i) => <div key={child.key ?? i} role="none" className="[&>button]:w-full [&_button]:outline-none">{child}</div>)}
        </div>,
        portalTarget,
      )}
    </div>
  );
}
